import { OBJECTIVES_OVERVIEW_SCALE } from './objectivesOverviewDensity';

import type { ObjectivesOverviewDensity } from './objectivesOverviewDensity';

import { useAmelioTranslations } from '@/hooks';
import { formatPercent } from '@/utils/numberFormat';
import { isProgressComplete } from '@/utils/objectiveProgress';
import { UNKNOWN_FIGURE } from '@/utils/objectivesSummary';

interface ObjectivesOverallProgressionProps {
    /** Unweighted mean of the employee's objective progressions, already rounded. */
    percentage: number;
    /** How many objectives the figure averages: with none, the band claims no state at all. */
    objectiveCount: number;
    /** Whether the objectives are still on their way; the figure is unknown until they land. */
    isLoading?: boolean;
    /** How tightly the band is drawn; `default` is the scale the profile's Objectives tab reads. */
    density?: ObjectivesOverviewDensity;
    dataTestId?: string;
}

/** The two ends of the gradient the bar and its figure share, from the objectives palette. */
const PROGRESS_GRADIENT = 'bg-gradient-to-r from-obj-accent-deep to-obj-accent';

/**
 * The wide gradient bar and headline figure at the top of the Objectives tab: one `progressbar`
 * for assistive technology, the figure beside it painted with the same gradient.
 */
export const ObjectivesOverallProgression = ({
    percentage,
    objectiveCount,
    isLoading = false,
    density = 'default',
    dataTestId = 'objectives-overall-progression',
}: ObjectivesOverallProgressionProps) => {
    const { t, i18n } = useAmelioTranslations();
    const width = Math.min(100, Math.max(0, percentage));
    const title = t('performanceProfile.objectives.overallProgression');
    const scale = OBJECTIVES_OVERVIEW_SCALE[density].progression;
    const showsLegend = !isLoading && objectiveCount > 0;
    const isComplete = isProgressComplete(width);

    return (
        <div
            className={'flex flex-col gap-3'}
            data-testid={dataTestId}
        >
            <div className={'flex items-center justify-between gap-3'}>
                <h3 className={`text-obj-muted font-medium ${scale.title}`}>{title}</h3>

                {showsLegend && (
                    <div className={'flex items-center gap-1.5'}>
                        <span
                            aria-hidden={'true'}
                            className={`${isComplete ? 'bg-primary' : 'bg-obj-accent'} size-2.5 rounded-full`}
                        />
                        <span className={`text-obj-muted font-medium ${scale.legend}`}>
                            {t(
                                isComplete
                                    ? 'performanceProfile.objectives.metrics.achieved'
                                    : 'performanceProfile.objectives.inProgressLegend',
                            )}
                        </span>
                    </div>
                )}
            </div>

            <div className={'flex items-center gap-4'}>
                <div
                    className={`${scale.track} ${scale.barShadow} flex-1 overflow-hidden rounded-full bg-white/45`}
                    role={'progressbar'}
                    // No `aria-valuenow` while the figure is unknown: a screen reader must hear
                    // "busy", not "zero percent". The same reason the figure beside it reads as a
                    // dash rather than 0 %.
                    aria-valuenow={isLoading ? undefined : width}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-busy={isLoading}
                    aria-label={title}
                >
                    <div
                        className={`${PROGRESS_GRADIENT} ${scale.barShadow} h-full rounded-full transition-[width] duration-700 ease-out`}
                        style={{ width: isLoading ? '0%' : `${width}%` }}
                        data-testid={`${dataTestId}-bar`}
                    />
                </div>

                <span
                    className={`${PROGRESS_GRADIENT} ${scale.value} bg-clip-text leading-none font-medium text-transparent tabular-nums`}
                    data-testid={`${dataTestId}-value`}
                >
                    {isLoading ? UNKNOWN_FIGURE : formatPercent(width, i18n.language)}
                </span>
            </div>
        </div>
    );
};
